import React, { Component } from "react";
import moment from "moment";

// Phase 2 — Veto Review rendered as one section per Veto Person (each covering
// the standards assigned to them in Phase 1). Only the section owner can fill
// and publish their own section; everyone else sees it read-only. Publishing a
// section (Approve / Need Revise) is immediate — there is no save-as-draft. When
// the last section is published the backend aggregates + advances the order.
const ACCEPT = ".xls,.xlsx,.xlsm,.xlsb";
const MAX_SIZE = 3 * 1024 * 1024; // 3MB

export default class VetoReviewSectionsForm extends Component {
  constructor(props) {
    super(props);
    this.state = { inputs: {}, submitting: null };
  }

  sectionByUser(uid) {
    return (this.props.order.veto_review || []).find((s) => s.user_id === uid) || {};
  }

  // Editable input for a section, defaulting to the section's persisted values
  // (used to pre-fill a reopened Need-Revise section).
  currentInput(uid) {
    if (this.state.inputs[uid]) {
      return this.state.inputs[uid];
    }
    const section = this.sectionByUser(uid);
    return {
      remarks: section.remarks || "",
      files: [],
      keepDocs: (section.documents || []).slice(),
    };
  }

  setInput(uid, patch) {
    this.setState((st) => {
      const section = (this.props.order.veto_review || []).find((s) => s.user_id === uid) || {};
      const base = st.inputs[uid] || {
        remarks: section.remarks || "",
        files: [],
        keepDocs: (section.documents || []).slice(),
      };
      return { inputs: { ...st.inputs, [uid]: { ...base, ...patch } } };
    });
  }

  onFiles(uid, fileList) {
    const files = Array.from(fileList || []);
    const tooBig = files.filter((f) => f.size > MAX_SIZE);
    if (tooBig.length) {
      window.alert(
        "Each file must be 3MB or smaller: " + tooBig.map((f) => f.name).join(", ")
      );
    }
    const ok = files.filter((f) => f.size <= MAX_SIZE);
    this.setInput(uid, { files: [...this.currentInput(uid).files, ...ok] });
  }

  removeNewFile(uid, idx) {
    const files = this.currentInput(uid).files.slice();
    files.splice(idx, 1);
    this.setInput(uid, { files });
  }

  removeKeepDoc(uid, idx) {
    const keepDocs = this.currentInput(uid).keepDocs.slice();
    keepDocs.splice(idx, 1);
    this.setInput(uid, { keepDocs });
  }

  publish(section, verdict) {
    const uid = section.user_id;
    const input = this.currentInput(uid);
    const fd = new FormData();
    fd.append("verdict", verdict);
    fd.append("remarks", input.remarks || "");
    (input.files || []).forEach((f) => fd.append("files[]", f));
    fd.append("keep_documents", JSON.stringify(input.keepDocs || []));

    this.setState({ submitting: uid });
    this.props
      .vetoSection(this.props.order.id, fd, this.props.fileAPI.accessToken)
      .then((res) => {
        this.setState({ submitting: null });
        if (res && res.data && res.data.finalized) {
          this.props.push("/orders/" + this.props.order.id);
        } else {
          this.props.fetchOrder(this.props.order.id, this.props.fileAPI.accessToken);
        }
      })
      .catch(() => this.setState({ submitting: null }));
  }

  downloadUrl(id) {
    return `${this.props.fileAPI.url}/download/${id}?token=${this.props.fileAPI.accessToken}`;
  }

  render() {
    const { order, user } = this.props;
    const sections = order.veto_review || [];
    const myId = user && user.id;
    const total = sections.length;
    const done = sections.filter((s) => s.status === "Published").length;
    const pending = sections
      .filter((s) => s.status !== "Published")
      .map((s) => s.name);

    if (total === 0) {
      return (
        <div className="alert alert-info">
          No Veto Person with an assigned standard on this order.
        </div>
      );
    }

    return (
      <div>
        {sections.map((section) => this.renderSection(section, myId))}
        <div className={`alert ${done === total ? "alert-success" : "alert-info"}`}>
          {done}/{total} section(s) published.
          {pending.length > 0 && (
            <span>
              {" "}
              Waiting on: <b>{pending.join(", ")}</b>
            </span>
          )}
        </div>
      </div>
    );
  }

  renderSection(section, myId) {
    const isOwner = Number(section.user_id) === Number(myId);
    const editable = isOwner && section.status !== "Published";
    return (
      <div key={section.user_id} className="panel panel-default">
        <div
          className="panel-heading"
          style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
        >
          <b>{section.name}</b>
          {isOwner && <span className="label label-primary">YOUR SECTION</span>}
          <span>
            {(section.standards || []).map((s) => (
              <span key={s.id} className="label label-success" style={{ marginLeft: 4 }}>
                {s.name}
              </span>
            ))}
          </span>
          {section.status === "Published" && (
            <span
              className="label"
              style={{
                marginLeft: "auto",
                background: section.verdict === "Approve" ? "#1e9e5a" : "#c0392b",
              }}
            >
              {section.verdict === "Approve" ? "APPROVED" : "NEED REVISE"}
            </span>
          )}
        </div>
        <div className="panel-body">
          {editable ? this.renderEditable(section) : this.renderReadonly(section)}
        </div>
      </div>
    );
  }

  renderEditable(section) {
    const uid = section.user_id;
    const input = this.currentInput(uid);
    const submitting = this.state.submitting === uid;
    return (
      <div className="form-horizontal">
        {section.verdict && (
          <div className="alert alert-warning" style={{ fontSize: 12 }}>
            Previous round verdict:{" "}
            <b>{section.verdict === "Approve" ? "Approve" : "Need Revise"}</b> — not
            carried over, choose again below.
          </div>
        )}
        <div className="form-group">
          <label className="col-sm-3 control-label">Veto &amp; Reviewer Feedback</label>
          <div className="col-sm-9">
            {(input.keepDocs || []).map((d, i) => (
              <div key={`k${i}`}>
                <a href={this.downloadUrl(d.id)}>
                  {d.detail && d.detail.name ? d.detail.name : "Document"}
                </a>{" "}
                <button
                  type="button"
                  className="btn btn-xs btn-link"
                  onClick={() => this.removeKeepDoc(uid, i)}
                >
                  remove
                </button>
              </div>
            ))}
            {(input.files || []).map((f, i) => (
              <div key={`f${i}`}>
                {f.name}{" "}
                <button
                  type="button"
                  className="btn btn-xs btn-link"
                  onClick={() => this.removeNewFile(uid, i)}
                >
                  remove
                </button>
              </div>
            ))}
            <input
              type="file"
              multiple
              accept={ACCEPT}
              onChange={(e) => this.onFiles(uid, e.target.files)}
            />
            <p className="help-block" style={{ fontSize: 11 }}>
              .xls/.xlsx/.xlsm/.xlsb, max 3MB each. Optional.
            </p>
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-3 control-label">Remarks</label>
          <div className="col-sm-9">
            <textarea
              className="form-control"
              rows={3}
              value={input.remarks}
              onChange={(e) => this.setInput(uid, { remarks: e.target.value })}
            />
          </div>
        </div>
        <div className="form-group">
          <div className="col-sm-offset-3 col-sm-9">
            <button
              type="button"
              className="btn btn-success"
              disabled={submitting}
              onClick={() => this.publish(section, "Approve")}
            >
              ✓ Approve
            </button>{" "}
            <button
              type="button"
              className="btn btn-danger"
              disabled={submitting}
              onClick={() => this.publish(section, "Need Revise")}
            >
              ✕ Need Revise
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderReadonly(section) {
    if (section.status !== "Published") {
      return (
        <div className="text-muted">
          <em>
            Not published yet — this section has no content until {section.name}{" "}
            clicks Approve or Need Revise.
          </em>
        </div>
      );
    }
    return (
      <div className="form-horizontal">
        <div className="form-group">
          <label className="col-sm-3 control-label">Veto &amp; Reviewer Feedback</label>
          <div className="col-sm-9" style={{ paddingTop: 7 }}>
            {(section.documents || []).length ? (
              (section.documents || []).map((d, i) => (
                <div key={i}>
                  <a href={this.downloadUrl(d.id)}>
                    ⬇ {d.detail && d.detail.name ? d.detail.name : "Document"}
                  </a>
                </div>
              ))
            ) : (
              <span className="text-muted">No file uploaded</span>
            )}
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-3 control-label">Remarks</label>
          <div className="col-sm-9" style={{ paddingTop: 7 }}>
            {section.remarks || <span className="text-muted">—</span>}
          </div>
        </div>
        <div className="text-muted" style={{ fontSize: 12, fontStyle: "italic" }}>
          Published by {section.published_by_name || "—"}
          {section.published_at
            ? " · " + moment.unix(section.published_at).format("DD MMM YYYY, HH:mm")
            : ""}
        </div>
      </div>
    );
  }
}